import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";
import { useFeatureFlagEnabled } from "posthog-js/react";

import { useMediaQuery } from "@mui/material";

import ExtendedTable from "components/Table";
import ExtendedList from "components/List";
import EmptyBlock from "components/EmptyBlock";
import { MenuItem } from "components/ActionMenu/types";
import ExtendedPagination from "components/Pagination";
import { TableHeadCell } from "components/Table/types";
import InvoiceModals from "../InvoiceModals";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  IbanInvoice,
  searchIbanInvoices,
  getLatestIbanInvoices,
  setIbanInvoiceFavorite,
  updateFavoriteIbanInvoiceByUid,
  setDuplicateInvoice,
  setSelectedInvoice,
  setShowQRCodeModal,
  setShowEditModal,
  setShowDeleteModal,
} from "store/features/ibanInvoiceSlice";
import { setNotification } from "store/features/notificationSlice";

import { ReactComponent as CopyIcon } from "assets/images/actionMenu/copy-menu-icon.svg";
import { ReactComponent as DuplicateIcon } from "assets/images/actionMenu/duplicate-menu-icon.svg";
import { ReactComponent as EditIcon } from "assets/images/actionMenu/edit-menu-icon.svg";
import { ReactComponent as DeleteIcon } from "assets/images/actionMenu/delete-menu-icon.svg";
import { ReactComponent as QrIcon } from "assets/images/actionMenu/qr-menu-icon.svg";

import { InvoiceFiltersState } from "../Main/InvoiceFilters/types";

import { routes } from "routes/routes";

import { getInvoiceColumns } from "shared/constants/ibanColumns";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";
import { formatInvoiceFilters } from "shared/functions/formatInvoiceFilters";
import { featureFlag } from "shared/constants/posthog";

interface InvoicesTableProps {
  loading?: boolean;
  ibanInvoices: IbanInvoice[] | null;

  pageSize?: number;
  pageNumber?: number;
  pageCount?: number;
  filters?: InvoiceFiltersState;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const InvoicesTable: React.FC<InvoicesTableProps> = (props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading, ibanInvoices } = props;

  const {
    pageNumber,
    pageCount,
    pageSize,
    filters,
    onPageChange,
    onPageSizeChange,
  } = props;

  const currentSubscription = useAppSelector(
    (state) => state.subscription.subscription,
  );

  // TODO: remove after feature release
  const showDetails = useFeatureFlagEnabled(
    featureFlag.invoice_details_feature_enabled,
  );

  const { isExpired, isTrial, paymentPlan } = currentSubscription || {};

  const isXs = useMediaQuery("(max-width: 1040px)");

  const onToggleFavorite = async (invoice: IbanInvoice) => {
    analytics().sendEvent(AnalyticsEvents.toggle_favorite_invoice);
    dispatch(
      setIbanInvoiceFavorite({ ...invoice, isFavorite: !invoice.isFavorite }),
    );

    try {
      await dispatch(
        updateFavoriteIbanInvoiceByUid({
          body: {
            ibanInvoiceUid: invoice.ibanQrInvoiceUid,
            isFavorite: !invoice.isFavorite,
          },
        }),
      ).unwrap();

      if (pageSize) {
        const formatedFilters = formatInvoiceFilters(filters);
        await dispatch(
          searchIbanInvoices({ pageSize, ...formatedFilters }),
        ).unwrap();
      } else {
        await dispatch(getLatestIbanInvoices()).unwrap();
      }
    } catch (err) {
      dispatch(
        setNotification({
          severity: "error",
          message: "Помилка оновлення улюбленого. Спробуйте пізніше.",
        }),
      );
      dispatch(setIbanInvoiceFavorite(invoice));
    }
  };

  const invoiceColumns = getInvoiceColumns(onToggleFavorite, showDetails);

  const getInvoiceColumn = (id: keyof IbanInvoice) =>
    invoiceColumns.find((column) => column.id === id);

  const compactColumns = (
    columns: Array<TableHeadCell<IbanInvoice> | undefined>,
  ) =>
    columns.filter((column): column is TableHeadCell<IbanInvoice> => !!column);

  const mobileHeading = compactColumns([getInvoiceColumn("isFavorite")]);
  const mobileBody = compactColumns([
    getInvoiceColumn("expirationDateTimeUtc"),
    getInvoiceColumn("iban"),
    getInvoiceColumn("paymentPurpose"),
    getInvoiceColumn("amount"),
    getInvoiceColumn("id"),
  ]);
  const mobileFooter = compactColumns([getInvoiceColumn("notes")]);

  const onQRCode = (invoice?: IbanInvoice) => {
    analytics().sendEvent(AnalyticsEvents.qrcode_invoice);

    invoice && dispatch(setSelectedInvoice(invoice));
    dispatch(setShowQRCodeModal(true));
  };

  const onDuplicateInvoice = (invoice?: IbanInvoice) => {
    analytics().sendEvent(AnalyticsEvents.dublicate_invoice);

    invoice && dispatch(setDuplicateInvoice(invoice));
    navigate(routes.createInvoice, { state: { from: location.pathname } });
  };

  const onUpdateInvoice = (invoice?: IbanInvoice) => {
    analytics().sendEvent(AnalyticsEvents.edit_invoice);

    invoice && dispatch(setSelectedInvoice(invoice));
    dispatch(setShowEditModal(true));
  };

  const onDeleteInvoice = (invoice?: IbanInvoice) => {
    analytics().sendEvent(AnalyticsEvents.delete_invoice);

    invoice && dispatch(setSelectedInvoice(invoice));
    dispatch(setShowDeleteModal(true));
  };

  const onAfterSubmit = useCallback(async () => {
    if (pageSize) {
      const formatedFilters = formatInvoiceFilters(filters);
      await dispatch(
        searchIbanInvoices({ pageSize, ...formatedFilters }),
      ).unwrap();
    } else {
      await dispatch(getLatestIbanInvoices()).unwrap();
    }
  }, [dispatch, filters, pageSize]);

  const onCopyInvoiceUrl = async (invoice?: IbanInvoice) => {
    analytics().sendEvent(AnalyticsEvents.copy_invoice);

    try {
      await navigator.clipboard.writeText(invoice?.ibanInvoiceUrl || "");
      dispatch(
        setNotification({
          severity: "success",
          message: "Посилання скопійоване",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          severity: "error",
          message: "Помилка копіювання",
        }),
      );
    }
  };

  const renderEmptyText = () => {
    const isPlanInactive =
      (paymentPlan?.paymentPlanId === 1 && !isTrial) || isExpired;

    if (isPlanInactive) {
      return "Історія IBAN посилань буде доступна після поновлення передплати.";
    }

    let isAnyFilterActive = false;
    if (filters) {
      const {
        searchText,
        ibans,
        identificationCodes,
        invoiceDateRange,
        expirationDateRange,
      } = filters;

      const isSearchActive = !!searchText;
      const areIbansActive = !!ibans && ibans.length > 0;
      const areIdCodesActive =
        !!identificationCodes && identificationCodes.length > 0;

      const isInvoiceDateActive = !!invoiceDateRange.range?.from;
      const isExpirationDateActive =
        !!expirationDateRange.range?.from || !!expirationDateRange.noExpiration;

      isAnyFilterActive =
        isSearchActive ||
        areIbansActive ||
        areIdCodesActive ||
        isInvoiceDateActive ||
        isExpirationDateActive;
    }

    if (isAnyFilterActive) {
      return `Результатів немає.\nЗмініть фільтри для пошуку результатів.`;
    }

    return "Ви ще не маєте жодного створеного посилання.";
  };

  const tableActions: MenuItem<IbanInvoice>[] = [
    {
      id: "copy",
      name: "Скопіювати посилання",
      icon: CopyIcon,
      action: onCopyInvoiceUrl,
      bottomDivider: true,
    },
    {
      id: "qr-code",
      name: "QR-код",
      icon: QrIcon,
      action: onQRCode,
    },
    {
      id: "dublicate",
      name: "Дублювати",
      icon: DuplicateIcon,
      action: onDuplicateInvoice,
    },
    {
      id: "edit",
      name: "Редагувати",
      icon: EditIcon,
      action: onUpdateInvoice,
    },
    {
      id: "delete",
      name: "Видалити",
      icon: DeleteIcon,
      action: onDeleteInvoice,
    },
  ];

  return (
    <>
      {!loading && ibanInvoices && ibanInvoices.length <= 0 ? (
        <EmptyBlock text={renderEmptyText()} />
      ) : (
        <>
          {isXs ? (
            <ExtendedList<IbanInvoice>
              loading={loading}
              skeletonRowCount={pageSize}
              heading={mobileHeading}
              body={mobileBody}
              footer={mobileFooter}
              rows={ibanInvoices || []}
              actions={tableActions}
              withDivider
            />
          ) : (
            <ExtendedTable<IbanInvoice>
              loading={loading}
              skeletonRowCount={pageSize}
              columns={invoiceColumns}
              rows={ibanInvoices || []}
              actions={tableActions}
            />
          )}
          {!loading && !!pageCount && !!pageNumber && onPageChange && (
            <ExtendedPagination
              id="invoices-pagination"
              pageSize={pageSize}
              pageCount={pageCount}
              pageNumber={pageNumber}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </>
      )}

      <InvoiceModals
        onAfterUpdateSubmit={onAfterSubmit}
        onAfterDeleteSubmit={onAfterSubmit}
      />
    </>
  );
};

export default InvoicesTable;
